import { z } from 'zod';

import { themeIdSchema } from './appearance.ts';

import { PERMISSION_LIST, type Permission } from '../permissions.ts';

const permission = z.enum(PERMISSION_LIST as [Permission, ...Permission[]]);

export const facilityRoleRefSchema = z.object({ facilityId: z.uuid(), roleId: z.uuid() });

export const createUserBodySchema = z.object({
  email: z.email().max(254),
  displayName: z.string().trim().min(1).max(120),
  /** A starting password the administrator gives the person. */
  password: z.string().min(12).max(256),
  facilityRoles: z.array(facilityRoleRefSchema).max(50),
});

export const updateUserStatusBodySchema = z.object({ status: z.enum(['active', 'disabled']) });

export const roleSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string(),
  requires2fa: z.boolean(),
  permissions: z.array(permission),
});

export type RoleView = z.infer<typeof roleSchema>;

export const createRoleBodySchema = z.object({
  name: z.string().trim().min(1).max(80),
  description: z.string().trim().max(300).default(''),
  requires2fa: z.boolean().default(false),
  permissions: z.array(permission).max(PERMISSION_LIST.length),
});

export const setRolePermissionsBodySchema = z.object({
  permissions: z.array(permission).max(PERMISSION_LIST.length),
});

/**
 * A role made from a standard template, with the permissions a release added to the template
 * that the role doesn't have and nobody has decided on yet (ADR 0031).
 */
export const rolePermissionOffersSchema = z.object({
  roleId: z.uuid(),
  roleName: z.string(),
  templateName: z.string(),
  permissions: z.array(z.object({ permission, description: z.string() })),
});

export type RolePermissionOffersView = z.infer<typeof rolePermissionOffersSchema>;

export const rolePermissionOffersListSchema = z.object({
  roles: z.array(rolePermissionOffersSchema),
});

export type RolePermissionOffersListView = z.infer<typeof rolePermissionOffersListSchema>;

/** Accept or decline offered permissions; each must be on offer to the role now. */
export const decideRolePermissionOffersBodySchema = z
  .object({
    accept: z.array(permission).max(PERMISSION_LIST.length).default([]),
    decline: z.array(permission).max(PERMISSION_LIST.length).default([]),
  })
  .refine((b) => b.accept.length + b.decline.length > 0, {
    message: 'Choose at least one permission to accept or decline.',
  })
  .refine((b) => !b.accept.some((p) => b.decline.includes(p)), {
    message: 'A permission can be accepted or declined, not both.',
  });

export type DecideRolePermissionOffersBody = z.input<typeof decideRolePermissionOffersBodySchema>;

/** KMHFL levels; the level decides which SHA services a facility may offer. */
export const FACILITY_LEVELS = ['2', '3A', '3B', '4', '5', '6'] as const;

export const facilitySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  mflCode: z.string().nullable(),
  level: z.enum(FACILITY_LEVELS),
  status: z.enum(['active', 'inactive']),
  /** `{facility}` in patient and visit number formats (ADR 0008). */
  numberCode: z.string().nullable(),
  /** The theme people here see until they choose their own (ADR 0030). */
  defaultTheme: themeIdSchema,
});

export type FacilityView = z.infer<typeof facilitySchema>;

/**
 * Master Facility List code. A facility keeps its place in the list without one until
 * it has been verified against the Facility Registry, so an empty box means "not known
 * yet", not an empty string.
 */
const mflCode = z
  .string()
  .trim()
  .max(20)
  .nullable()
  .transform((value) => (value === null || value === '' ? null : value));

/**
 * The short code patient and visit numbers carry for this facility. Unique in the
 * organisation; changing it changes new numbers only, never ones already issued.
 */
export const NUMBER_CODE_PATTERN = /^[A-Za-z0-9]{1,10}$/;
const numberCode = z
  .string()
  .trim()
  .regex(NUMBER_CODE_PATTERN, 'Use 1 to 10 letters or digits, with no spaces');

export const createFacilityBodySchema = z.object({
  name: z.string().trim().min(1).max(160),
  mflCode: mflCode.default(null),
  /** Left out, the database gives the next free F1, F2, … */
  numberCode: numberCode.optional(),
  level: z.enum(FACILITY_LEVELS),
  status: z.enum(['active', 'inactive']).default('active'),
});

/** Only the fields sent are changed; the rest are left alone. */
export const updateFacilityBodySchema = z.object({
  name: z.string().trim().min(1).max(160).optional(),
  mflCode: mflCode.optional(),
  level: z.enum(FACILITY_LEVELS).optional(),
  status: z.enum(['active', 'inactive']).optional(),
  numberCode: numberCode.optional(),
});

export const organisationSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  status: z.enum(['active', 'suspended']),
  /** Sign out after this many idle minutes (ADR 0003: 15 by default, 5–480 allowed). */
  sessionIdleMinutes: z.number().int(),
});

export type OrganisationView = z.infer<typeof organisationSchema>;

export const SESSION_IDLE_MINUTES = { min: 5, max: 480 } as const;

export const updateOrganisationBodySchema = z.object({
  sessionIdleMinutes: z.number().int().min(SESSION_IDLE_MINUTES.min).max(SESSION_IDLE_MINUTES.max),
});
